import { TextField } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { DEFAULT_STICKY_NOTE_COLOR, STICKY_NOTE_SIZE_CONFIG } from '@/components/Recipe/Views/StickyNoteView';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { FlexCol } from '@/UI/styles';
import { getContrastedTextColorFromHex } from '@/utils/color.utils';
import { useAvoidNodeOverflowOnPaste } from '@/hooks/useAvoidNodeOverflowOnPaste';
import { useNode, useStickyNoteView } from '../Recipe/FlowContext';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import type { StickyNoteData } from '@/types/node';
import type { NodeId } from 'web';

interface StickyNoteNodeProps {
  id: NodeId;
  data: StickyNoteData;
}

function StickyNoteNode({ id, data }: StickyNoteNodeProps) {
  const stickyNoteView = useStickyNoteView(id);
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasTextareaFocusedRef = useRef(false);
  const wasNodeSelectedRef = useRef(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const isNodeSelected = Boolean(useNode(id).selected);
  const { maxHeight, onPaste } = useAvoidNodeOverflowOnPaste({
    nodeId: id,
    textareaRef,
  });

  const backgroundColor = data.color || DEFAULT_STICKY_NOTE_COLOR;
  const currentSize = stickyNoteView.getSize();
  const { fontSize, padding, width, minRows } = STICKY_NOTE_SIZE_CONFIG[currentSize];

  const onNoteChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      stickyNoteView.updateNote(event.target.value);
    },
    [stickyNoteView],
  );

  const { textColor, placeholderColor } = getContrastedTextColorFromHex(backgroundColor);
  const outlineColor = isTextareaFocused ? color.Yellow64_T : color.Yellow100;

  const onWrapperPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Store whether node was ALREADY selected before this interaction
    wasNodeSelectedRef.current = isNodeSelected;

    // Store initial pointer position to detect drag
    dragStartPos.current = { x: event.clientX, y: event.clientY };

    // triggered before onBlur of the textarea, avoid flickering when clicking on the padding box around the textarea
    wasTextareaFocusedRef.current = isTextareaFocused;
  };

  const onWrapperPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartPos.current) {
      const dx = Math.abs(event.clientX - dragStartPos.current.x);
      const dy = Math.abs(event.clientY - dragStartPos.current.y);

      // If moved more than 5px threshold, it's a drag
      if (dx > 5 || dy > 5) {
        dragStartPos.current = null; // Mark as dragged
      }
    }
  };

  const onWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // If it was a drag, ignore the click event
    if (!dragStartPos.current) {
      return;
    }
    dragStartPos.current = null;

    const textFieldElement = textareaRef.current;
    if (!textFieldElement) return;

    // If node wasn't selected before this click, this is the FIRST interaction
    // React Flow will select it, Don't focus textarea yet
    if (!wasNodeSelectedRef.current) {
      return;
    }

    // Node was already selected, this is the SECOND click
    // Now we can focus the textarea
    const rect = textFieldElement.getBoundingClientRect();
    const clickX = event.clientX;
    const clickY = event.clientY;

    const isInsideTextField =
      clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;

    if (isTextareaFocused) {
      // Already focused, clicking outside textarea should blur it
      if (!isInsideTextField) {
        textFieldElement.blur();
      }
    } else if (!wasTextareaFocusedRef.current) {
      // Second click on selected node -> focus textarea
      textFieldElement.focus();
    }

    wasTextareaFocusedRef.current = false;
  };

  return (
    <DynamicNode2
      id={id}
      data={data}
      hideTitle
      sx={{
        backgroundColor,
        border: 'none',
        outline: isNodeSelected ? `2px solid ${outlineColor}` : 'none',
        outlineOffset: isNodeSelected ? '-2px' : undefined,
        borderRadius: 1,
        padding: 0,
        width,
        justifyContent: 'flex-start',
        cursor: isNodeSelected ? 'grab' : 'default',
        transition: 'background-color 0.2s ease-in-out',
      }}
    >
      <FlexCol
        ref={wrapperRef}
        onClick={onWrapperClick}
        onPointerDown={onWrapperPointerDown}
        onPointerMove={onWrapperPointerMove}
        sx={{
          width: '100%',
          pointerEvents: role === 'guest' ? 'none' : undefined,
          p: `${padding}px`,
        }}
      >
        <TextField
          className={isTextareaFocused ? 'nodrag nopan' : ''}
          onFocus={() => {
            setIsTextareaFocused(true);
          }}
          onBlur={() => {
            setIsTextareaFocused(false);
          }}
          onPaste={onPaste}
          inputRef={textareaRef}
          value={data?.result?.note || ''}
          onChange={onNoteChange}
          multiline
          minRows={minRows}
          placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.PLACEHOLDER)}
          fullWidth
          size="small"
          onMouseDown={(event) => event.stopPropagation()}
          slotProps={{
            input: {
              sx: {
                p: 0,
                color: textColor,
                fontSize: `${fontSize}px`,
                fontWeight: 400,
                maxHeight: maxHeight ? `${maxHeight - padding * 2}px` : undefined,
                overflowY: maxHeight ? 'hidden' : undefined,
                pointerEvents: isTextareaFocused ? 'auto' : 'none',
                '&::placeholder': {
                  color: placeholderColor,
                  opacity: 1,
                },
              },
            },
          }}
          sx={{
            p: 0,
            pointerEvents: isTextareaFocused ? 'auto' : 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />
      </FlexCol>
    </DynamicNode2>
  );
}

export default StickyNoteNode;
